import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  alpha,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
} from '@mui/material';
import Drawer from './StyledDrawer';
import { useAtom, useSetAtom } from 'jotai';
import {
  searchValuesAtom,
  selectedBranchAtom,
  selectedCustomerIdAtom,
} from '../../Atoms';
import useCallHub from '../Hubs/CallHubProvider';
import { StyledList } from '../../Theme/StyledComponents';
import {
  APP_ROUTES,
  ROUTE_ICONS,
  ROUTES_TREE,
  FULLPAGE_ROUTES,
} from '../../Router/routes';
import RouteListItem from './RouteListItem';
import { CHANNEL_TYPES } from '../../Utils/constants/general';
import FiberNewRoundedIcon from '@mui/icons-material/FiberNewRounded';
import { compareDates } from '../../Helpers/dates';
import Blink from '../Blink';
import SidebarHeader from './SidebarHeader';
import { renderRoutesTree } from '../../Router/routes';

const Sidebar = function Sidebar({
  handleChangeSection,
  toggleSidebar,
  open = false,
  version = '',
  userRoles = null,
}) {
  const location = useLocation().pathname;
  const navigate = useNavigate();
  const theme = useTheme();

  const newReleaseReadOn = localStorage.getItem('newReleaseReadOn');

  const setSearchValues = useSetAtom(searchValuesAtom);
  const [selectedRoute, setSelectedRoute] = useState(location);
  const [selectedBranch, setSelectedBranch] = useAtom(selectedBranchAtom);
  useCallHub(selectedBranch);
  const [newReleaseReadOnState, setNewReleaseReadOnState] = useState(
    newReleaseReadOn || null
  );
  const [selectedCustomerId, setSelectedCustomerId] = useAtom(
    selectedCustomerIdAtom
  );

  const handleListItemClick = (page) => {
    if (selectedRoute !== page) {
      handleChangeSection(page);
      setSelectedRoute(page);
    }
  };

  const handleSelectBranch = (newBranch) => {
    if (selectedCustomerId) {
      setSelectedCustomerId('');
      navigate('/'); //when here will be redirected to allClientsPage
    }
    setSelectedBranch(newBranch);
    sessionStorage.setItem('selectedBranch', JSON.stringify(newBranch));
  };

  useEffect(() => {
    setSelectedRoute(location);
  }, [location]);

  useEffect(() => {
    setSearchValues({});
    navigate(APP_ROUTES.CUSTOMERS);
  }, [selectedBranch?.channelType]);

  return (
    <Drawer
      open={open}
      anchor="left"
      variant="persistent"
      slotProps={{ paper: { variant: 'elevation', elevation: 3 } }}
      sx={{
        display:
          (Object.keys(APP_ROUTES).find(
            (key) =>
              APP_ROUTES[key] === location && FULLPAGE_ROUTES.includes(key)
          ) ||
            location === '/401') &&
          'none',
        padding: '16px 0px 16px 16px',
      }}
    >
      <SidebarHeader
        open={open}
        toggleSidebar={toggleSidebar}
        handleSelectBranch={handleSelectBranch}
      />
      <div
        className="sidebar-scroll"
        style={{ overflowY: 'auto', overflowX: 'hidden' }}
      >
        <StyledList>
          {renderRoutesTree(
            ROUTES_TREE,
            selectedBranch?.channelType === CHANNEL_TYPES.Long
          ).map((routeName) => {
            const { id, subRoutes, defaultOpen } = ROUTES_TREE[routeName];
            const routePath = APP_ROUTES[routeName];
            const Icon = ROUTE_ICONS[routeName];
            return (
              <RouteListItem
                key={id}
                routeName={routeName}
                routePath={routePath}
                icon={<Icon />}
                selected={selectedRoute}
                onClick={handleListItemClick}
                subRoutes={subRoutes}
                userRoles={userRoles}
                sidebarOpen={open}
                defaultOpen={defaultOpen}
                isLongChannel={
                  selectedBranch?.channelType === CHANNEL_TYPES.Long
                }
              />
            );
          })}
        </StyledList>
      </div>
      {version && (
        <ListItem
          sx={{
            marginTop: 'auto',
            padding: '8px',
            visibility: open ? 'visible' : 'hidden',
            transition: 'visibility .3s',
          }}
          secondaryAction={
            compareDates(
              new Date(newReleaseReadOnState),
              new Date(version?.releasedOn),
              'smaller'
            ) ? (
              <Blink id="new-icon" ratio={2} unit={1}>
                <FiberNewRoundedIcon color="warning" />
              </Blink>
            ) : null
          }
        >
          <ListItemButton
            component="a"
            href="https://docs.databeasy.it/blog"
            sx={{
              borderRadius: '8px',
              ...(compareDates(
                new Date(newReleaseReadOnState),
                new Date(version?.releasedOn),
                'smaller'
              )
                ? {
                    backgroundColor: alpha(theme.palette.warning.main, 0.1),
                    border: `2px solid ${theme.palette.warning.main}`,
                  }
                : {}),
            }}
            onClick={() => {
              localStorage.setItem('newReleaseReadOn', new Date());
              setNewReleaseReadOnState(new Date());
            }}
            target="_blank"
            disabled={
              !compareDates(
                new Date(newReleaseReadOnState),
                new Date(version?.releasedOn),
                'smaller'
              )
            }
          >
            <ListItemText
              // eslint-disable-next-line no-undef
              primary={`${ENV !== 'production' ? ENV : ''} ${version?.major}.${version?.minor}.${version?.revision}`}
              slotProps={{ primary: { sx: { textTransform: 'uppercase' } } }}
            />
          </ListItemButton>
        </ListItem>
      )}
    </Drawer>
  );
};

Sidebar.propTypes = {
  handleChangeSection: PropTypes.func.isRequired,
  open: PropTypes.bool,
  drawerWidth: PropTypes.number.isRequired,
  closeSidebar: PropTypes.func.isRequired,
  desktop: PropTypes.bool,
  userRoles: PropTypes.array,
  version: PropTypes.string,
};

export default Sidebar;
